import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import {
  IB_INCOME_REPORTS_SEARCH_TERM,
  useIBIncomeFetch,
} from '@pages/IntroducingBroker/Reports/Income/hooks/useIBIncomeFetch'

import { AuthSessionContext } from '@context/AuthContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { useTableSearch } from '@ui/v2/Table/hooks/useTableSearch'
import { FilterOptions } from '@ui/v2/TableFilter/TableFilter'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { conditionalReverse } from '@utils/array'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { prepareExportVisibleDataPayload } from '@utils/queryParser'

import { DealDto } from '@model/DealDto'
import { ExportHubReportSectionId } from '@model/ExportHub'
import { TradingAccountType } from '@model/MultiTierDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { IBReportResultsPage } from '../IBReportResultsPage'
import { useBrokerSummaryFetch } from '../hooks/useClientsOverallSummary'
import { IBIncomeTable, IBIncomeTableSortKeys } from './IBIncomeTable'
import { IncomeColumnCtx, incomeColumns } from './IBIncomeTable.columns'

const useIncomeSearch = () => {
  const location = useLocation()
  return useTableSearch<IBIncomeTableSortKeys, DealDto, IncomeColumnCtx>({
    columns: incomeColumns,
    placeholderKey: 'IB.Name or account number',
    searchOptions: { initialSearchTerm: location?.state?.TradingAccountName },
  })
}

const useClientsGenerateDocumentFile = (data: DealDto[]) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatNumber } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()

  const tableBody = () => {
    const headerCsvData = [
      conditionalReverse(
        [
          t('Trading Account.Trading Account'),
          t('IB.Name'),
          t('IB.Date & Time'),
          t('IB.Currency'),
          t('Trading Account.Account Type'),
          t('IB.Instrument'),
          t('IB.Volume'),
          t('IB.Commission'),
          t('IB.Spread Revenue'),
          t('IB.Entity'),
        ],
        isArabic
      ),
    ]

    return data.reduce((previousValue, currentValue) => {
      const isAccountTypeClassic = TradingAccountType.Classic === currentValue.tradingAccountType.id

      return previousValue.concat([
        conditionalReverse(
          [
            currentValue.tradingAccount.name,
            currentValue.client.name,
            formatDate(currentValue.tradedAt, { formatType: dateFormat }),
            currentValue.currency.id,
            currentValue.tradingAccountType.name,
            currentValue.instrument.name,
            formatNumber(currentValue.volume),
            formatNumber(currentValue.amount),
            isAccountTypeClassic ? formatNumber(currentValue.spreadRevenue ?? 0) : t('N/A'),
            currentValue.tickmillCompany?.name || t('N/A'),
          ],
          isArabic
        ),
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('ib_income_report', tableBody())
}

const useFilterOptions = () => {
  const apiClient = useApiClient(ClientApiClient)

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.ib.incomeFilterOptions(),
    queryFn: () => apiClient.getIBIncomeFilterOptions(),
  })

  return { filterOptions }
}

export const IBIncomePage: React.FC = () => {
  const { t } = useTranslation()

  const search = useIncomeSearch()
  const brokerSummary = useBrokerSummaryFetch({ search })
  const introducingBrokerIncome = useIBIncomeFetch({ search })
  const generateDocumentFile = useClientsGenerateDocumentFile(introducingBrokerIncome.query.data)
  const { filterOptions } = useFilterOptions()

  return (
    <IBReportResultsPage
      title={t('IB.Income')}
      data={introducingBrokerIncome}
      summary={
        <TableSummary
          isLoading={brokerSummary.isLoading || introducingBrokerIncome.query.isLoading}
          summaryItems={brokerSummary.summaryItems}
        />
      }
      exportData={{
        section: ExportHubReportSectionId.Income,
        filters: {
          dateFrom: (search.search['ca_search_DateFrom'] as string) || null,
          dateTo: (search.search['ca_search_DateTo'] as string) || null,
          search: prepareExportVisibleDataPayload<DealDto>(
            introducingBrokerIncome.query,
            search.search,
            { [IB_INCOME_REPORTS_SEARCH_TERM]: search.searchTerm }
          ),
        },
      }}
      search={search}
      generateDocumentFile={generateDocumentFile}
      scrollToHeaderId={ScrollToIds.IBSignupHeader}
      filterOptions={filterOptions}
      filterOptionsTypes={[
        FilterOptions.CAClientVolume,
        FilterOptions.CACommission,
        FilterOptions.AccountType,
        FilterOptions.Date,
        FilterOptions.Entity,
      ]}
      table={<IBIncomeTable data={introducingBrokerIncome} search={search} />}
    />
  )
}
